import moment from 'moment';
import * as R from 'ramda';

import { momentNow } from 'models/date';

import { toArray } from 'modules/utils/list';

import {
    patchDistribution,
    getProducts as getProductsAPI,
    getDistributionById,
} from 'api/distributions';

export const workflow = [
    'sale.preparation',
    'sale.opened',
    'sale.finalization',
    'distribution.opened',
    'distribution.finalized',
];

const preparationStatuses = ['sale.init', 'sale.preparation', 'sale.canceled'];

function step(stepName) {
    return workflow.indexOf(stepName);
}

function getTimeObject(timeString) {
    const timeArray = timeString.split(':');
    return {
        hours: timeArray[0],
        minutes: timeArray[1],
        seconds: timeArray[2],
    };
}

export function getDistributionStart(distribution) {
    return distribution.distributionDate;
}

export function getDistributionStartsIn(distribution) {
    return moment(distribution.distributionDate).from(momentNow());
}

export function getDistributionEnd(distribution) {
    if (distribution.distributionEndDate) {
        return moment.utc(distribution.distributionEndDate).toISOString();
    }

    const timeEnd = getTimeObject(distribution.timeEnd);
    return moment(distribution.distributionDate)
        .set(timeEnd)
        .toISOString();
}

export function getDayBeforeSaleEndDate(distribution) {
    return moment(distribution.saleEndDate)
        .subtract(4, 'hours')
        .toISOString();
}

export function groupByDayAndTimeStarts(distributions) {
    const distributionsByDay = R.pipe(
        R.groupBy(distrib => moment(distrib.distributionDate).format('YYYY-MM-DD')),
        R.toPairs,
        R.map(([date, distributionsAtDay]) => ({ date, distributionsAtDay })),
        R.sortBy(R.prop('date'))
    )(distributions);

    return distributionsByDay.map(day => {
        return {
            date: day.date,
            timeStarts: groupByTimeStarts(day.distributionsAtDay),
        };
    });
}

export function groupByTimeStarts(distributionList) {
    return R.pipe(
        R.groupBy(R.prop('timeStart')),
        R.toPairs,
        R.map(([timeStart, distributions]) => ({ timeStart, distributions })),
        R.sortBy(R.prop('timeStart'))
    )(distributionList);
}

export function saleCanBePrepared(distribution) {
    return (
        preparationStatuses.indexOf(distribution.status) !== -1 &&
        (!distribution.saleEndDate || momentNow().isBefore(distribution.saleEndDate)) // Not sure why we need to check that
    );
}

export function saleCanStart(distribution) {
    return distribution.status === 'sale.preparation' && distribution.isOpenable === true;
}

export function saleHasStarted(distribution) {
    return step(distribution.status) >= step('sale.opened');
}

export function producerRequestedProductsRemovalFromSale(distribution) {
    return distribution.productsRemoval && distribution.productsRemoval.requestedAt;
}

export function producerRemovedProductsFromSale(distribution) {
    return distribution.productsRemoval && distribution.productsRemoval.doneAt;
}

/**
 * Check if a producer has effectively withdrawn their products from the sale.
 * Returns true if:
 * - The removal has been completed (doneAt is set), OR
 * - A removal was requested and the scheduled date has passed
 *
 * @param {Object} producer - Producer object with productsRemoval property
 * @returns {boolean}
 */
export function hasEffectiveProductsRemoval(producer) {
    if (!producer.productsRemoval) {
        return false;
    }

    // If removal is done, it's definitely effective
    if (producer.productsRemoval.doneAt) {
        return true;
    }

    // If removal was requested and the scheduled date has passed, it's effective
    if (producer.productsRemoval.requestedAt) {
        return moment(producer.productsRemoval.requestedAt).isSameOrBefore(momentNow());
    }

    return false;
}

/**
 * Check if a producer has scheduled a withdrawal for a future date.
 * Returns true if:
 * - A removal was requested (requestedAt is set)
 * - The removal hasn't been completed yet (doneAt is null)
 * - The scheduled date is in the future
 *
 * @param {Object} producer - Producer object with productsRemoval property
 * @returns {boolean}
 */
export function hasScheduledProductsRemoval(producer) {
    if (!producer.productsRemoval || !producer.productsRemoval.requestedAt) {
        return false;
    }

    if (producer.productsRemoval.doneAt) {
        return false;
    }

    return moment(producer.productsRemoval.requestedAt).isAfter(momentNow());
}

export function saleHasEnded(distribution) {
    return step(distribution.status) > step('sale.opened');
}

export const saleIsOpen = distribution => !saleHasEnded(distribution);

export function distributionCanStart(distribution) {
    return (
        distribution.status === 'opened' && momentNow().isBefore(getDistributionStart(distribution))
    );
}

export function distributionHasStarted(distribution) {
    return (
        step(distribution.status) >= step('distribution.opened') &&
        momentNow().isAfter(getDistributionStart(distribution))
    );
}

export function distributionHasEnded(distribution) {
    return (
        step(distribution.status) >= step('distribution.opened') &&
        momentNow().isAfter(getDistributionEnd(distribution))
    );
}

export function transfersCanStart(distribution) {
    return distribution.status === 'sale.finalized';
}

export function isLastDay(distribution) {
    const now = momentNow();
    const saleEndDay = getDayBeforeSaleEndDate(distribution);
    const hoursUntilSaleEnd = moment(saleEndDay).diff(now, 'hours');

    return hoursUntilSaleEnd < 24;
}

export function getSaleEndDateInfo(distribution) {
    const now = momentNow();
    const realSaleEndDate = distribution.saleEndDate;
    const saleEndDay = getDayBeforeSaleEndDate(distribution);
    const hoursUntilSaleEnd = moment(saleEndDay).diff(now, 'hours');

    return {
        isLastDay: hoursUntilSaleEnd < 24,
        isLastTwoDays: hoursUntilSaleEnd < 48,
        closingInReal: moment(realSaleEndDate).from(now),
        closingInMember: moment(realSaleEndDate)
            .subtract(3, 'hours')
            .from(now), // strip the extra 3 hours for members
        saleEndDay,
    };
}

export function hasSomeNotEndedSales(distributions) {
    return !distributions.every(saleHasEnded);
}

export function productAlreadyRemoved(distribution) {
    return R.not(R.pathEq(['productsRemoval', 'doneAt'], null, distribution));
}

export function finalizeSale(distributionId) {
    return patchDistribution(distributionId, { action: 'distribution_open_action' });
}

export function getCurrentDistributions(assembly) {
    return R.pipe(
        R.path(['_links', 'currentSale']),
        // If there is only one distribution, currentSale is an object, otherwise it's an array… '-_-
        toArray,
        R.sortBy(R.prop('distributionDate'))
    )(assembly);
}

export const getNextSaleToPrepare = nextSalesToPrepare => {
    const nextSaleInPreparation = R.find(
        R.both(
            x => x.id !== null,
            y => y.status !== 'sale.canceled'
        )
    )(nextSalesToPrepare);
    const nextSaleToPrepare = R.find(R.propEq('status', 'sale.init'))(nextSalesToPrepare);

    return nextSaleInPreparation || nextSaleToPrepare;
};

export const getProducts = (distributionId, user, params = {}) => {
    const { perPage = 10000, page = 1, onlyHighlighted = false } = params;
    const payload = { perPage, page, category: '_all' };
    if (onlyHighlighted) {
        payload.onlyHighlighted = true;
    }
    if (!user.anonymous) {
        payload.withAlreadyBoughtProductsInfo = true;
    }
    return getProductsAPI(distributionId, payload);
};

export const getDistributionDetails = (distributionId, view = 'flat') =>
    getDistributionById(distributionId, { view });

export const getMaxPossibleRemovalDateFromDistributions = distributions => {
    if (R.isEmpty(distributions)) {
        return null;
    }
    const maxSaleEndDatesForSimpleDistributions = R.pipe(
        R.map(distribution => moment(distribution.saleEndDate)),
        moment.max
    )(distributions);
    return maxSaleEndDatesForSimpleDistributions;
};

export const getPossibleSendingMessageDays = distribution => {
    const limit = moment(distribution.lastDayToSendMail).startOf('day');
    const today = moment().startOf('day');
    let current = moment(distribution.lastDayToSendMail)
        .subtract(3, 'days')
        .startOf('day');
    let days = [];

    if (current < today) {
        current = today;
    }

    while (current <= limit) {
        days = R.append(current.format('YYYY-MM-DD'), days);
        current = current.add(1, 'days');
    }

    return days;
};
